import { useEffect, useState } from "react";
import { api, DELIVERY_STATUSES, labelOf } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { useNavigate } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";

const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function Dispatches() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const nav = useNavigate();

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data } = await api.get("/dispatches", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [status, dateFrom, dateTo]);

  const clearAll = () => { setQ(""); setStatus(""); setDateFrom(""); setDateTo(""); };
  const hasFilter = q || status || dateFrom || dateTo;

  return (
    <div data-testid="page-dispatches" className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Operations</div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 mt-1">Dispatches</h1>
        </div>
        <button data-testid="btn-nav-new-dispatch" onClick={() => nav("/dispatches/new")} className="bg-[var(--navy)] hover:bg-slate-800 text-white text-sm font-semibold px-4 py-2.5 rounded-md flex items-center gap-2">
          <Plus size={16} /> New Dispatch
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Search LR / Invoice / PO</label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="input-dispatch-search"
                value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="e.g. LR-98765  ·  INV-2026-001  ·  PO-1234"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none"
              />
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Type any part of your LR No., SRAD Invoice No., PO No. or dispatch code.</div>
          </div>
          <div>
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Status</label>
            <select data-testid="select-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="">All Statuses</option>
              {DELIVERY_STATUSES.map((s) => <option key={s} value={s}>{labelOf(s)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Dispatch From</label>
            <input data-testid="input-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
          <div>
            <label className="text-[11px] uppercase font-semibold tracking-widest text-slate-500">Dispatch To</label>
            <input data-testid="input-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md text-sm" />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <button onClick={load} className="text-sm bg-[var(--navy)] text-white rounded-md px-3 py-2">Apply</button>
          {hasFilter && (
            <button data-testid="btn-clear-filters" onClick={clearAll} className="text-sm text-slate-600 border border-slate-300 rounded-md px-3 py-2 flex items-center gap-1">
              <X size={12} /> Clear filters
            </button>
          )}
          <div className="ml-auto text-xs text-slate-500">{items.length} result{items.length === 1 ? "" : "s"}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 text-left">
              <th className="px-5 py-3">Dispatch</th><th className="px-5 py-3">Invoice</th>
              <th className="px-5 py-3">PO</th><th className="px-5 py-3">LR</th>
              <th className="px-5 py-3">Amount</th><th className="px-5 py-3">Dispatch Date</th>
              <th className="px-5 py-3">Expected</th><th className="px-5 py-3">Status</th><th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} data-testid={`dispatch-row-${d.id}`} className="table-row border-t border-slate-100">
                <td className="px-5 py-3 font-mono text-xs">{d.dispatch_code}</td>
                <td className="px-5 py-3">{d.invoice_number}<div className="text-xs text-slate-500">{d.invoice_date}</div></td>
                <td className="px-5 py-3 text-slate-600">{d.client_po_number || "-"}</td>
                <td className="px-5 py-3 font-mono text-xs">{d.lr_number}</td>
                <td className="px-5 py-3">{fmtInr(d.invoice_amount)}</td>
                <td className="px-5 py-3 text-slate-600 text-xs">{d.dispatch_date || "-"}</td>
                <td className="px-5 py-3 text-slate-600 text-xs">{d.expected_delivery || "-"}</td>
                <td className="px-5 py-3"><StatusBadge status={d.delivery_status} /></td>
                <td className="px-5 py-3 text-right">
                  <button data-testid={`btn-open-dispatch-${d.id}`} onClick={() => nav(`/dispatches/${d.id}`)} className="text-xs text-amber-700 hover:text-amber-900 font-semibold">Open →</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan="9" className="py-10 text-center text-slate-500 text-sm">{hasFilter ? "No dispatches match your filters" : "No dispatches yet"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
